import {
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { t } from "i18next";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { FiEdit2, FiRepeat } from "react-icons/fi";
import { AdminOrgDto, useAdminListOrganizationsQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { Paging } from "../../../common/Paging";
import { AdminChangeOwnerModal } from "./AdminChangeOwnerModal";
import { AdminEditOrganizationDrawer } from "./AdminEditOrganizationDrawer";
import { AdminImpersonateUserModal } from "./AdminImpersonateUserModal";
import { getOrgAccessValues, OrgAccessGroup, useOrgAccessGroups } from "./useOrgAccessGroups";

const VISIBLE_CHIPS = 4;

export const OrgsList = () => {
  const authState = useLoggedInWithOrgContextState();
  const [skip, setSkip] = useState(0);
  const [limit] = useState(10);
  const [name, setName] = useState("");
  const [debouncedName, setDebouncedName] = useState("");

  const debounced = useMemo(
    () =>
      debounce((v: string) => {
        setDebouncedName(v.trim());
      }, 300),
    []
  );
  useEffect(() => {
    debounced(name);
    return () => debounced.cancel();
  }, [name, debounced]);

  const { data, isLoading, isFetching } = useAdminListOrganizationsQuery({
    organizationId: authState.selectedOrg.id,
    skip,
    limit,
    name: debouncedName || undefined,
  });
  const [updateOrgId, setUpdateOrgId] = useState<string>();
  const [impersonateOrgId, setImpersonateOrgId] = useState<string>();
  const [changeOwnerOrgId, setChangeOwnerOrgId] = useState<string>();
  const groups = useOrgAccessGroups();

  if (!data) return <div>{t("Loading")}...</div>;

  const updateOrganization = data.orgs.find((org) => org.id === updateOrgId);
  const impersonateOrganization = data.orgs.find((org) => org.id === impersonateOrgId);
  const changeOwnerOrganization = data.orgs.find((org) => org.id === changeOwnerOrgId);

  return (
    <>
      {updateOrganization && (
        <AdminEditOrganizationDrawer
          organization={updateOrganization}
          onClose={() => setUpdateOrgId(undefined)}
          onImpersonate={() => setImpersonateOrgId(updateOrganization.id)}
          onChangeOwner={() => setChangeOwnerOrgId(updateOrganization.id)}
        />
      )}
      {impersonateOrganization && (
        <AdminImpersonateUserModal
          organization={impersonateOrganization}
          onClose={() => setImpersonateOrgId(undefined)}
        />
      )}
      {changeOwnerOrganization && (
        <AdminChangeOwnerModal
          organization={changeOwnerOrganization}
          onClose={() => setChangeOwnerOrgId(undefined)}
        />
      )}
      <InputGroup size="sm" mb="4">
        <InputLeftElement>
          <Icon as={FaSearch} color="smMuted" />
        </InputLeftElement>
        <Input
          placeholder={String(t("Search by name"))}
          value={name}
          onChange={(e) => {
            setSkip(0);
            setName(e.target.value);
          }}
        />
      </InputGroup>
      <TableContainer border="1px solid" borderColor="smBorder" rounded="md">
        <Table size="sm">
          <Thead bg="smBackgroundSecondary">
            <Tr>
              <Th>{t("Organization")}</Th>
              <Th>{t("Owner")}</Th>
              <Th>{t("Modules")}</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {data.orgs.map((org) => (
              <OrgRow
                key={org.id}
                org={org}
                groups={groups}
                onEdit={() => setUpdateOrgId(org.id)}
                onImpersonate={() => setImpersonateOrgId(org.id)}
              />
            ))}
            {data.orgs.length === 0 && (
              <Tr>
                <Td colSpan={4}>
                  <Text color="smMuted">{t("No organizations found")}</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Paging limit={limit} skip={skip} setSkip={setSkip} isLoading={isLoading || isFetching} count={data.count} />
    </>
  );
};

const OrgRow = ({
  org,
  groups,
  onEdit,
  onImpersonate,
}: {
  org: AdminOrgDto;
  groups: OrgAccessGroup[];
  onEdit: () => void;
  onImpersonate: () => void;
}) => {
  const values = getOrgAccessValues(org);
  const enabledFlags = groups.flatMap((group) =>
    group.flags.filter((flag) => values[flag.key]).map((flag) => ({ ...flag, chipBg: group.chipBg, chipFg: group.chipFg }))
  );
  const visibleFlags = enabledFlags.slice(0, VISIBLE_CHIPS);
  const overflowCount = enabledFlags.length - visibleFlags.length;

  return (
    <Tr onClick={onEdit} cursor="pointer" _hover={{ bg: "smBackgroundSecondary" }}>
      <Td>
        <Text fontWeight="semibold">{org.name}</Text>
        <Text fontSize="xs" color="smMuted">
          {org.country.name}
        </Text>
      </Td>
      <Td>
        <Text>{`${org.owner.firstName ?? ""} ${org.owner.lastName ?? ""}`.trim()}</Text>
        <Text fontSize="xs" color="smMuted">
          {org.owner.email}
        </Text>
      </Td>
      <Td>
        <HStack spacing="1">
          {visibleFlags.map((flag) => (
            <Tag key={flag.key} size="sm" rounded="full" bg={flag.chipBg} color={flag.chipFg} fontWeight="bold">
              {flag.label}
            </Tag>
          ))}
          {overflowCount > 0 && (
            <Tag size="sm" rounded="full" bg="smBackgroundSecondary" color="smMuted" fontWeight="bold">
              +{overflowCount}
            </Tag>
          )}
          {enabledFlags.length === 0 && (
            <Tag size="sm" rounded="full" bg="smBackgroundSecondary" color="smMuted">
              {t("No modules")}
            </Tag>
          )}
        </HStack>
      </Td>
      <Td textAlign="end" onClick={(e) => e.stopPropagation()}>
        <IconButton aria-label={t("Edit")} icon={<FiEdit2 />} size="sm" variant="ghost" onClick={onEdit} />
        <IconButton
          aria-label={t("Impersonate")}
          icon={<FiRepeat />}
          size="sm"
          variant="ghost"
          onClick={onImpersonate}
        />
      </Td>
    </Tr>
  );
};
